import { Component, Injector, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ITableOptions } from '@modeso/aumico-lib-core-fe';
import {
    GetOrganizationChecklistDto,
    OrganizationChecklistActions,
    OrganizationChecklistState,
    selectAllOrganizationChecklists,
    selectChecklistById,
} from '@modeso/aumico-lib-users-fe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store, select } from '@ngrx/store';
import { cloneDeep, omit } from 'lodash-es';
import { BehaviorSubject, Observable, filter, map, take, tap } from 'rxjs';
import { Paths } from '../../../../enums/paths.enum';
import { RouteLabels } from '../../../../enums/routeLabels.enum';
import { BasePageComponent } from '../../../../pages/base.page';

@UntilDestroy()
@Component({
    selector: 'app-check-list-management',
    templateUrl: './check-list-management.page.html',
    styleUrls: ['./check-list-management.page.scss'],
})
export class CheckListManagementPage extends BasePageComponent implements OnInit {
    public filterSubject = new BehaviorSubject<string>(undefined);
    public organizationChecklists$: Observable<Partial<GetOrganizationChecklistDto[]>>;
    public options: ITableOptions;
    public pageHeader = {
        isLocalizedKey: true,
        value: RouteLabels.CHECKLISTS,
    };

    public breadcrumbItems = [
        {
            displayName: RouteLabels.CHECKLISTS,
        },
    ];

    public checkListNameSet: Set<string>;
    public userLanguage: string;

    constructor(
        private store: Store<OrganizationChecklistState>,
        private injector: Injector,
        private readonly route: ActivatedRoute,
    ) {
        super(injector);

        this.userLanguage = this.i18n.language.toLowerCase();

        this.organizationChecklists$ = this.store.pipe(
            select(selectAllOrganizationChecklists),
            tap((checklists) => {
                this.checkListNameSet = new Set(checklists.map((checklist) => checklist.name));
            }),
            map((checklists) =>
                checklists.map((checklist) => ({
                    ...checklist,
                    disableSelection: checklist.isDefault,
                    updatedBy: checklist.isDefault ? 'aumico' : checklist.updatedBy,
                })),
            ),
        );

        this.actionHeaderService.onActionEvent$.pipe(untilDestroyed(this)).subscribe(this.onAction.bind(this));
    }

    public onEditChecklist($event: GetOrganizationChecklistDto): void {
        this.router.navigate([`./${Paths.EDIT}/${$event.id}`], { relativeTo: this.route });
    }

    public onCopyChecklist($event: GetOrganizationChecklistDto): void {
        this.store
            .select(selectChecklistById($event.id))
            .pipe(
                filter((checklist) => checklist !== null),
                take(1),
            )
            .subscribe((checklist: GetOrganizationChecklistDto) => {
                const copyName = this.getCopyName(checklist.name);
                const copyChecklist = omit(cloneDeep(checklist), 'id', 'name');
                copyChecklist.name = copyName;

                this.store.dispatch(
                    OrganizationChecklistActions.createChecklist({ createChecklistDto: copyChecklist }),
                );
            });
    }

    override ngOnInit(): void {
        this.store.dispatch(OrganizationChecklistActions.loadOrganizationChecklists());
    }

    private getCopyName(checklistName: string): string {
        let suffix = 1;

        let postSuffixCheckListName = `${checklistName} (${suffix})`;

        if (!this.checkListNameSet.has(postSuffixCheckListName)) {
            return postSuffixCheckListName;
        }

        while (this.checkListNameSet.has(`${postSuffixCheckListName}`)) {
            suffix++;
            postSuffixCheckListName = `${checklistName} (${suffix})`;
        }

        return postSuffixCheckListName;
    }

    public onDeleteChecklist(selectedIds: string[]): void {
        if (selectedIds && selectedIds.length > 0) {
            this.store.dispatch(OrganizationChecklistActions.deleteChecklists({ ids: selectedIds }));
        }
    }

    public onAddNewChecklistClicked(): void {
        this.router.navigate([`./${Paths.ADD}`], { relativeTo: this.route });
    }
}
